/**
 * Welcome step of the onboarding wizard
 * Contains form fields for name and description
 */
import { ChangeEvent, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import {
  DevHubAlert,
  DevHubBox,
  DevHubGrid,
  DevHubLink,
  DevHubTypography,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { OnboardingFormData, TouchedFields } from '../types';
import {
  nameValidation,
  getNameErrorMessage,
  descriptionValidation,
  getDescriptionErrorMessage,
} from '../validation';
import { checkApplicationNameUniqueness } from '../../../../../util/applicationValidation';
import { ConfigHelper } from '@devxdiscover/devhub-common';

interface WelcomeStepProps {
  formData: OnboardingFormData;
  touchedFields: TouchedFields;
  onFormChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleFieldBlur: (fieldName: keyof TouchedFields) => void;
  nameUniquenessError?: string;
  setNameUniquenessError: (error: string | undefined) => void;
  isCheckingName: boolean;
  setIsCheckingName: (checking: boolean) => void;
}

/**
 * Welcome step component with form fields
 * @param props - Component props
 */
export const WelcomeStep = ({
  formData,
  touchedFields,
  onFormChange,
  handleFieldBlur,
  nameUniquenessError: externalNameUniquenessError,
  setNameUniquenessError,
  isCheckingName: externalIsCheckingName,
  setIsCheckingName,
}: WelcomeStepProps) => {
  const slackUrl = ConfigHelper.instance.slackFeedbackUrl;

  // Check name uniqueness on field blur
  const handleNameBlur = useCallback(
    async (name: string) => {
      handleFieldBlur('name');

      // Skip if name has basic validation errors or is empty
      if (!name || nameValidation(name)) {
        setNameUniquenessError(undefined);
        return;
      }

      setIsCheckingName(true);
      setNameUniquenessError(undefined);

      try {
        const result = await checkApplicationNameUniqueness(name);
        if (!result.isUnique) {
          setNameUniquenessError(result.error);
        } else {
          setNameUniquenessError(undefined);
        }
      } catch (error) {
        // Handle error silently
      } finally {
        setIsCheckingName(false);
      }
    },
    [setNameUniquenessError, setIsCheckingName, handleFieldBlur],
  );

  // Determine if name field has error
  const hasNameError =
    touchedFields.name &&
    (nameValidation(formData.name) || !!externalNameUniquenessError);

  // Get appropriate error message
  const getNameHelperText = () => {
    if (!touchedFields.name) {
      return '';
    }
    if (nameValidation(formData.name)) {
      return getNameErrorMessage(formData.name);
    }
    if (externalNameUniquenessError) {
      return externalNameUniquenessError;
    }
    if (externalIsCheckingName) {
      return 'Checking name availability...';
    }
    return '';
  };
  return (
    <DevHubGrid container>
      <DevHubGrid item xs={12}>
        <DevHubBox>
          <DevHubTypography variant="body1">
            Quick-start temporary experimental applications with GitHub
            repository, CNAP experimentation environment, and AD security groups
            — ideal for Lab Week projects and short-term trials.
          </DevHubTypography>
        </DevHubBox>
      </DevHubGrid>

      {/* Form Fields - Name */}
      <DevHubGrid item xs={12}>
        <DevHubBox>
          <DevHubTextField
            size="medium"
            variant="outlined"
            margin="none"
            name="name"
            value={formData.name}
            onChange={onFormChange}
            onBlur={() => handleNameBlur(formData.name)}
            error={hasNameError}
            required
            label="Name"
            placeholder="Give your idea a name."
            fullWidth
            noMargin
            helperText={getNameHelperText()}
          />
        </DevHubBox>
      </DevHubGrid>

      {/* Form Fields - Description */}
      <DevHubGrid item xs={12}>
        <DevHubBox>
          <DevHubTextField
            variant="outlined"
            size="medium"
            margin="none"
            required
            name="description"
            value={formData.description}
            error={
              touchedFields.description &&
              descriptionValidation(formData.description)
            }
            onChange={onFormChange}
            onBlur={() => {
              // Only mark as touched if field has content
              if (formData.description.trim().length > 0) {
                handleFieldBlur('description');
              }
            }}
            label="Description"
            placeholder="Tell us about your idea."
            fullWidth
            noMargin
            helperText={
              touchedFields.description &&
              descriptionValidation(formData.description)
                ? getDescriptionErrorMessage(formData.description)
                : ''
            }
          />
        </DevHubBox>
      </DevHubGrid>

      {/* Compact Resources Section */}
      <DevHubGrid item xs={12}>
        <DevHubBox tbMargin="medium">
          <DevHubTypography variant="body2" fontColor="blue" weight="bold">
            You'll automatically receive:
          </DevHubTypography>
        </DevHubBox>
        <DevHubBox
          padding="small"
          borderRadius="8px"
          bgcolor="#f8f9fa"
          border="1px solid #e0e0e0"
          mb={1.5}
        >
          <DevHubGrid container spacing={3} alignItems="center">
            {/* GitHub Repo */}
            <DevHubGrid item xs={4}>
              <DevHubGrid container spacing={1} alignItems="center">
                <DevHubGrid item>
                  <img
                    src="/icons/github.svg"
                    alt="GitHub"
                    style={{ width: '24px', height: '24px' }}
                  />
                </DevHubGrid>
                <DevHubGrid item xs>
                  <DevHubTypography variant="body2" fontColor="purpleBlue">
                    GitHub Repository
                  </DevHubTypography>
                  <DevHubTypography variant="caption" fontColor="medGray">
                    Source Control
                  </DevHubTypography>
                </DevHubGrid>
              </DevHubGrid>
            </DevHubGrid>

            {/* CNAP Tenancy */}
            <DevHubGrid item xs={4}>
              <DevHubGrid container spacing={1} alignItems="center">
                <DevHubGrid item>
                  <img
                    src="/icons/cnap.svg"
                    alt="CNAP"
                    style={{ width: '24px', height: '24px' }}
                  />
                </DevHubGrid>
                <DevHubGrid item xs>
                  <DevHubTypography variant="body2" fontColor="orangeRed">
                    CNAP Tenancy
                  </DevHubTypography>
                  <DevHubTypography
                    variant="caption"
                    style={{ color: '#999', fontSize: '0.75rem' }}
                  >
                    Deployment Env
                  </DevHubTypography>
                </DevHubGrid>
              </DevHubGrid>
            </DevHubGrid>

            {/* AD Groups */}
            <DevHubGrid item xs={4}>
              <DevHubGrid container spacing={1} alignItems="center">
                <DevHubGrid item>
                  <img
                    src="/icons/azure.svg"
                    alt="Azure AD"
                    style={{ width: '24px', height: '24px' }}
                  />
                </DevHubGrid>
                <DevHubGrid item xs>
                  <DevHubTypography variant="body2" fontColor="blueGreen">
                    AD Groups
                  </DevHubTypography>
                  <DevHubTypography
                    variant="caption"
                    style={{ color: '#999', fontSize: '0.75rem' }}
                  >
                    Security Groups
                  </DevHubTypography>
                </DevHubGrid>
              </DevHubGrid>
            </DevHubGrid>
          </DevHubGrid>
        </DevHubBox>
      </DevHubGrid>

      {/* Note */}
      <DevHubGrid item xs={12}>
        <DevHubTypography
          variant="body2"
          style={{
            color: '#666',
            fontSize: '0.9rem',
            marginBottom: '12px',
            padding: '12px',
            backgroundColor: '#fff3cd',
            borderLeft: '4px solid #ffc107',
            borderRadius: '4px',
          }}
        >
          Designed for short-lived experiments. Additional resources beyond
          GitHub, CNAP, and Active Directory may not be available. Experimental
          app IDs may not be recognized by downstream provisioning systems.
          <br />
          <br />
          <strong>Need a permanent app?</strong> Go to{' '}
          <DevHubLink
            component={NavLink}
            to="/onboarding"
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              fontWeight: 500,
            }}
          >
            Onboarding
          </DevHubLink>
          .
        </DevHubTypography>
      </DevHubGrid>

      {/* Feedback Section */}
      <DevHubGrid item xs={12}>
        <DevHubAlert severity="info">
          <DevHubTypography variant="body2">
            Need other resources or looking for something else?{' '}
            <DevHubLink
              href={slackUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Share your feedback
            </DevHubLink>{' '}
            to help us improve.
          </DevHubTypography>
        </DevHubAlert>
      </DevHubGrid>
    </DevHubGrid>
  );
};
